/**
 * AddressFieldType — F12.B T12.B.3.
 *
 * DDL: 4 osobne kolumny per pole adresowe (multi-column):
 *   <slug>__street TEXT, <slug>__city TEXT, <slug>__postal VARCHAR(20), <slug>__country VARCHAR(2)
 *
 * Frontend przekazuje/odbiera obiekt { street, city, postalCode, country }.
 * API (records route) zajmuje się pakowaniem/rozpakowaniem do/z kolumn DDL.
 */
import { MapPin } from 'lucide-react';

import { FieldRegistry } from '../FieldRegistry';

interface AddressValue {
  street?: string;
  city?: string;
  postalCode?: string;
  country?: string;
}

const INPUT_CLASS =
  'w-full rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-500 focus:ring-1 focus:ring-accent-500 focus:outline-none';

FieldRegistry.register({
  type: 'address',
  renderInput: ({ field, value, onChange }) => {
    const addr: AddressValue = (value as AddressValue) || {
      street: '',
      city: '',
      postalCode: '',
      country: (field as { country_default?: string }).country_default ?? 'Polska',
    };

    const set = (key: keyof AddressValue, val: string) => onChange({ ...addr, [key]: val });

    return (
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">Ulica</label>
          <input
            type="text"
            value={addr.street ?? ''}
            onChange={(e) => set('street', e.target.value)}
            className={INPUT_CLASS}
            placeholder="ul. Przykładowa 123"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-text-secondary">
              Kod pocztowy
            </label>
            <input
              type="text"
              value={addr.postalCode ?? ''}
              onChange={(e) => set('postalCode', e.target.value)}
              className={INPUT_CLASS}
              placeholder="00-000"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-text-secondary">Miasto</label>
            <input
              type="text"
              value={addr.city ?? ''}
              onChange={(e) => set('city', e.target.value)}
              className={INPUT_CLASS}
              placeholder="Warszawa"
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">Kraj</label>
          <input
            type="text"
            value={addr.country ?? ''}
            onChange={(e) => set('country', e.target.value)}
            className={INPUT_CLASS}
            placeholder="Polska"
          />
        </div>
      </div>
    );
  },
  renderCell: (_field, value) => {
    if (!value) return '—';
    if (typeof value === 'string') {
      return (
        <div className="flex items-start gap-1.5">
          <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-text-muted mt-0.5" />
          <span className="truncate text-sm text-text-primary">{value}</span>
        </div>
      );
    }
    const a = value as AddressValue;
    const parts = [a.street, [a.postalCode, a.city].filter(Boolean).join(' '), a.country].filter(
      Boolean,
    );
    if (parts.length === 0) return '—';
    return (
      <div className="flex items-start gap-1.5">
        <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-text-muted mt-0.5" />
        <span className="truncate text-sm text-text-primary" title={parts.join(', ')}>
          {parts.join(', ')}
        </span>
      </div>
    );
  },
  validate: (value, field) => {
    if (!value) return field.isRequired ? 'Pole wymagane' : null;
    const a = value as AddressValue;
    if (a.country === 'Polska' && a.postalCode) {
      if (!/^\d{2}-\d{3}$/.test(a.postalCode)) return 'Kod pocztowy powinien być w formacie XX-XXX';
    }
    return null;
  },
  defaultValue: { street: '', city: '', postalCode: '', country: 'Polska' },
});
